import { useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Role cache to prevent excessive database calls
const roleCache = new Map<string, { isAdmin: boolean; timestamp: number }>();
const ROLE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Check if role cache is valid
  const getCachedRole = useCallback((userId: string): boolean | null => {
    const cached = roleCache.get(userId);
    if (cached && Date.now() - cached.timestamp < ROLE_CACHE_TTL) {
      return cached.isAdmin;
    }
    return null;
  }, []);

  // Set role in cache
  const setCachedRole = useCallback((userId: string, isAdmin: boolean) => {
    roleCache.set(userId, { isAdmin, timestamp: Date.now() });
  }, []);

  // Clear role cache for a user
  const clearRoleCache = useCallback((userId: string) => {
    roleCache.delete(userId);
  }, []);

  // Secure admin role check with caching
  const checkAdminRole = useCallback(async (userId: string) => {
    try {
      // Check cache first
      const cachedRole = getCachedRole(userId);
      if (cachedRole !== null) {
        setIsAdmin(cachedRole);
        setLoading(false);
        return;
      }

      // Query database using the secure has_role function
      // This uses RLS and the security definer function
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle(); // Use maybeSingle to avoid errors when no row exists

      if (error) {
        console.error('Role check error:', error.message);
        setIsAdmin(false);
        setCachedRole(userId, false);
      } else {
        const hasAdminRole = !!data;
        setIsAdmin(hasAdminRole);
        setCachedRole(userId, hasAdminRole);
      }
    } catch (err) {
      console.error('Admin role check failed');
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [getCachedRole, setCachedRole]);

  // Handle session changes
  const handleSessionChange = useCallback((session: Session | null) => {
    setSession(session);
    setUser(session?.user ?? null);
    setAuthError(null);

    if (session?.user) {
      // Verify session is not expired
      const expiresAt = session.expires_at;
      if (expiresAt && Date.now() / 1000 > expiresAt) {
        console.warn('Session expired, signing out');
        signOut();
        return;
      }

      checkAdminRole(session.user.id);
    } else {
      setIsAdmin(false);
      setLoading(false);
    }
  }, [checkAdminRole]);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT') {
          // Clear all state on sign out
          setUser(null);
          setSession(null);
          setIsAdmin(false);
          if (session?.user?.id) {
            clearRoleCache(session.user.id);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          // Token was refreshed, update session
          handleSessionChange(session);
        } else {
          handleSessionChange(session);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Session retrieval error:', error.message);
        setAuthError('Failed to retrieve session');
        setLoading(false);
        return;
      }
      handleSessionChange(session);
    });

    return () => subscription.unsubscribe();
  }, [handleSessionChange, clearRoleCache]);

  // Secure sign out function
  const signOut = useCallback(async () => {
    try {
      // Clear role cache before signing out
      if (user?.id) {
        clearRoleCache(user.id);
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error.message);
        setAuthError('Failed to sign out');
      }

      // Clear state regardless of API response
      setUser(null);
      setSession(null);
      setIsAdmin(false);
    } catch (err) {
      console.error('Sign out failed');
      // Force clear state even on error
      setUser(null);
      setSession(null);
      setIsAdmin(false);
    }
  }, [user?.id, clearRoleCache]);

  // Refresh role check (useful after role changes)
  const refreshRole = useCallback(async () => {
    if (user?.id) {
      clearRoleCache(user.id);
      await checkAdminRole(user.id);
    }
  }, [user?.id, clearRoleCache, checkAdminRole]);

  return {
    user,
    session,
    loading,
    isAdmin,
    authError,
    signOut,
    refreshRole,
  };
};